// src/pages/analytics/index.jsx
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Grid, CardActionArea, useTheme, alpha, Box, 
  Typography, Paper, Grow, Fade, Zoom, Divider, 
  Chip, Button, Avatar
} from '@mui/material';

// Import styled components
import {
  PageContainer,
  SectionContainer,
  SectionTitle,
  TruncatedText,
  EntityCard,
  EntityCardContent
} from '../../components/ui';
import {
  Dashboard as DashboardIcon,
  Campaign as CampaignIcon,
  Article as ContentIcon,
  Business as BrandIcon,
  People as ClientIcon,
  Public as SocialIcon,
  BarChart as CustomReportIcon,
  TrendingUp as TrendingUpIcon,
  PieChart as AudienceIcon,
  MonetizationOn as FinancialIcon,
  Insights as CompetitiveIcon,
  Language as RegionalIcon,
  Assessment as ActivityIcon,
  Analytics as AnalyticsIcon,
  MoreHoriz as MoreIcon,
  Check as CheckIcon,
  AutoGraph as AutoGraphIcon,
  ViewQuilt as ViewQuiltIcon,
  AutoAwesome as MagicIcon,
  BoltOutlined as BoltIcon,
  LiveHelp as HelpIcon,
  Star as StarIcon,
  NewReleases as NewReleasesIcon,
  Timeline as TimelineIcon,
  Email as EmailIcon,
  AccountBalance as TaxIcon
} from '@mui/icons-material';

/**
 * Analytics Index Page - Entry point for all analytics features
 */
const AnalyticsIndex = () => {
  const theme = useTheme();
  
  // Analytics modules
  const modules = [
    {
      title: 'Dashboard',
      description: 'Overview of key performance metrics',
      icon: <DashboardIcon fontSize="large" />,
      color: theme.palette.primary.main,
      link: '/analytics/dashboard'
    },
    {
      title: 'Campaign Analytics',
      description: 'Analyze campaign performance and ROI',
      icon: <CampaignIcon fontSize="large" />,
      color: theme.palette.secondary.main,
      link: '/analytics/campaigns'
    },
    {
      title: 'Content Analytics',
      description: 'Track content engagement and reach',
      icon: <ContentIcon fontSize="large" />,
      color: theme.palette.success.main,
      link: '/analytics/content'
    },
    {
      title: 'Brand Analytics',
      description: 'Monitor brand growth and perception',
      icon: <BrandIcon fontSize="large" />,
      color: theme.palette.info.main,
      link: '/analytics/brands'
    },
    {
      title: 'Client Analytics',
      description: 'Review client-specific performance',
      icon: <ClientIcon fontSize="large" />,
      color: theme.palette.warning.main,
      link: '/analytics/clients'
    },
    {
      title: 'Social Media',
      description: 'Social media performance metrics',
      icon: <SocialIcon fontSize="large" />,
      color: theme.palette.error.main,
      link: '/analytics/social'
    },
    {
      title: 'Audience Analytics',
      description: 'Audience demographics and behavior insights',
      icon: <AudienceIcon fontSize="large" />,
      color: '#009688', // teal
      link: '/analytics/audience'
    },
    {
      title: 'Financial Analytics',
      description: 'Revenue tracking and financial performance',
      icon: <FinancialIcon fontSize="large" />,
      color: '#4caf50', // green
      link: '/analytics/financial'
    },
    {
      title: 'User Activity',
      description: 'Monitor system usage and user actions',
      icon: <ActivityIcon fontSize="large" />,
      color: '#ff5722', // deep orange
      link: '/analytics/user-activity'
    },
    {
      title: 'Competitive Analysis',
      description: 'Track performance against competitors',
      icon: <CompetitiveIcon fontSize="large" />,
      color: '#673ab7', // deep purple
      link: '/analytics/competitive'
    },
    {
      title: 'Regional Analytics',
      description: 'Geographical performance and regional insights',
      icon: <RegionalIcon fontSize="large" />,
      color: '#2196f3', // blue
      link: '/analytics/regional'
    },
    {
      title: 'Performance Trends',
      description: 'Long-term trend analysis and forecasting',
      icon: <TrendingUpIcon fontSize="large" />,
      color: '#9c27b0', // purple
      link: '/analytics/trends'
    },
    {
      title: 'Tax Analytics',
      description: 'Comprehensive tax reporting and compliance tracking',
      icon: <TaxIcon fontSize="large" />,
      color: '#4CAF50', // green
      link: '/analytics/tax'
    },
    {
      title: 'Custom Reports',
      description: 'Build customized analytics reports',
      icon: <CustomReportIcon fontSize="large" />,
      color: '#795548', // brown
      link: '/analytics/custom-report'
    }
  ];

  return (
    <PageContainer>
      <Fade in={true} timeout={800}>
        <Paper 
          elevation={0} 
          sx={{ 
            p: 3, 
            mb: 4, 
            borderRadius: theme?.aghrba?.tokens?.borderRadius?.card || 16,
            background: theme.palette.mode === 'dark' 
              ? `linear-gradient(120deg, ${alpha(theme.palette.primary.dark, 0.5)}, ${alpha(theme.palette.secondary.dark, 0.5)})`
              : `linear-gradient(120deg, ${alpha(theme.palette.primary.light, 0.1)}, ${alpha(theme.palette.secondary.light, 0.1)})`,
            position: 'relative',
            overflow: 'hidden',
            border: `1px solid ${alpha(theme.palette.divider, theme?.aghrba?.tokens?.alpha?.border || 0.1)}`,
            transition: theme?.aghrba?.tokens?.transitions?.standard || 'all 0.3s ease',
          }}
        >
          {/* Abstract shapes for decoration */}
          <Box 
            sx={{ 
              position: 'absolute', 
              top: -30, 
              right: -30, 
              width: 180, 
              height: 180, 
              borderRadius: '50%', 
              background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.08)}, transparent 70%)`,
              zIndex: 0,
            }} 
          />
          
          <Box 
            sx={{ 
              position: 'absolute', 
              bottom: -50, 
              left: -30, 
              width: 200, 
              height: 200, 
              borderRadius: '50%', 
              background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main, 0.1)}, transparent 70%)`,
              zIndex: 0,
            }} 
          />
          
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
              <Avatar 
                sx={{ 
                  width: 56, 
                  height: 56, 
                  background: theme.palette.mode === 'dark'
                    ? `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`
                    : `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  boxShadow: `0 4px 20px 0 ${alpha(theme.palette.primary.main, 0.2)}`,
                }}
              >
                <AnalyticsIcon fontSize="large" />
              </Avatar>
              
              <Box>
                <Typography 
                  variant="h4" 
                  component="h1" 
                  sx={{ 
                    fontWeight: 700,
                    background: theme.palette.mode === 'dark'
                      ? `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`
                      : `linear-gradient(90deg, ${theme.palette.primary.dark}, ${theme.palette.secondary.dark})`,
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  Analytics & Reporting <MagicIcon sx={{ ml: 1, fontSize: 20 }} />
                </Typography>
                
                <Typography
                  variant="subtitle1"
                  color="textSecondary"
                  sx={{ 
                    mt: 0.5,
                    maxWidth: '800px'
                  }}
                >
                  Monitor performance, track engagement, and gain insights with our comprehensive analytics tools.
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 3 }}>
              <Chip 
                icon={<CheckIcon />} 
                label="Real-time data" 
                size="small" 
                sx={{ 
                  background: alpha(theme.palette.success.main, 0.1),
                  borderColor: alpha(theme.palette.success.main, 0.2),
                  color: theme.palette.success.main,
                  '& .MuiChip-icon': { color: theme.palette.success.main }
                }}
              />
              <Chip 
                icon={<CheckIcon />} 
                label="Customizable reports" 
                size="small" 
                sx={{ 
                  background: alpha(theme.palette.info.main, 0.1),
                  borderColor: alpha(theme.palette.info.main, 0.2),
                  color: theme.palette.info.main,
                  '& .MuiChip-icon': { color: theme.palette.info.main }
                }}
              />
              <Chip 
                icon={<CheckIcon />} 
                label="Data visualizations" 
                size="small" 
                sx={{ 
                  background: alpha(theme.palette.warning.main, 0.1),
                  borderColor: alpha(theme.palette.warning.main, 0.2),
                  color: theme.palette.warning.main,
                  '& .MuiChip-icon': { color: theme.palette.warning.main }
                }}
              />
              <Chip 
                icon={<CheckIcon />} 
                label="Export capabilities" 
                size="small" 
                sx={{ 
                  background: alpha(theme.palette.secondary.main, 0.1),
                  borderColor: alpha(theme.palette.secondary.main, 0.2),
                  color: theme.palette.secondary.main,
                  '& .MuiChip-icon': { color: theme.palette.secondary.main }
                }}
              />
            </Box>
          </Box>
        </Paper>
      </Fade>
      
      <Typography
        variant="h6"
        fontWeight="600"
        sx={{ 
          mb: 2.5, 
          pl: 1,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <ViewQuiltIcon sx={{ mr: 1.5, color: theme.palette.primary.main }} /> 
        Select Analytics Module
      </Typography>

      <Grid container spacing={3}>
        {modules.map((module, index) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
            <Zoom in={true} style={{ transitionDelay: `${index * 50}ms` }}>
              <Paper
                component={RouterLink}
                to={module.link}
                elevation={1}
                sx={{
                  textDecoration: 'none',
                  height: '100%',
                  borderRadius: theme?.aghrba?.tokens?.borderRadius?.card || 16,
                  overflow: 'hidden',
                  position: 'relative',
                  transition: theme?.aghrba?.tokens?.transitions?.standard || 'all 0.3s ease',
                  border: `1px solid ${alpha(theme.palette.divider, theme?.aghrba?.tokens?.alpha?.border || 0.08)}`,
                  background: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.paper, 0.8)
                    : theme.palette.background.paper,
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 12px 20px -10px ${alpha(module.color, 0.3)}`,
                    '& .iconContainer': {
                      transform: 'scale(1.1)',
                    },
                    '& .hoverIndicator': {
                      width: '100%',
                    }
                  },
                }}
              >
                {/* Hover indicator */}
                <Box 
                  className="hoverIndicator"
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '3px',
                    width: '0%',
                    background: `linear-gradient(90deg, ${module.color}, ${alpha(module.color, 0.6)})`,
                    transition: 'width 0.3s ease',
                    zIndex: 2,
                  }}
                />

                {/* Badge for new modules */}
                {(module.title === 'Audience Analytics' || 
                  module.title === 'Financial Analytics' || 
                  module.title === 'User Activity' || 
                  module.title === 'Competitive Analysis' || 
                  module.title === 'Regional Analytics' ||
                  module.title === 'Tax Analytics') && (
                  <Chip
                    label="New"
                    size="small"
                    icon={<NewReleasesIcon fontSize="small" />}
                    sx={{
                      position: 'absolute',
                      top: 12,
                      right: 12,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      background: theme.palette.mode === 'dark'
                        ? `linear-gradient(135deg, ${alpha('#ff5722', 0.9)}, ${alpha('#ff9800', 0.9)})`
                        : `linear-gradient(135deg, ${alpha('#ff5722', 0.9)}, ${alpha('#ff9800', 0.9)})`,
                      color: '#fff',
                      zIndex: 2,
                      border: 'none',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                      '& .MuiChip-icon': {
                        color: '#fff',
                        fontSize: '0.9rem',
                      },
                    }}
                  />
                )}

                <CardActionArea sx={{ height: '100%', p: 2.5, pb: 3 }}>
                  <Box
                    className="iconContainer"
                    sx={{
                      width: 65,
                      height: 65,
                      borderRadius: theme?.aghrba?.tokens?.borderRadius?.medium || 8,
                      background: `linear-gradient(135deg, ${module.color}, ${alpha(module.color, 0.7)})`,
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                      transition: theme?.aghrba?.tokens?.transitions?.standard || 'all 0.3s ease',
                      boxShadow: `0 4px 15px ${alpha(module.color, 0.35)}`,
                    }}
                  >
                    {module.icon}
                  </Box>

                  <Typography 
                    variant="h6" 
                    component="h2" 
                    fontWeight="600"
                    color="textPrimary"
                    gutterBottom
                    sx={{ 
                      fontSize: '1.1rem',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    {module.title}
                  </Typography>

                  <Typography 
                    variant="body2" 
                    color="textSecondary"
                    sx={{
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      lineHeight: 1.4,
                    }}
                  >
                    {module.description}
                  </Typography>
                </CardActionArea>
              </Paper>
            </Zoom>
          </Grid>
        ))}
      </Grid>
      
      <Box sx={{ mt: 6 }}>
        <Fade in={true} timeout={1000}>
          <Paper 
            elevation={1} 
            sx={{
              p: 3,
              borderRadius: theme?.aghrba?.tokens?.borderRadius?.card || 16,
              background: theme.palette.mode === 'dark'
                ? `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.2)}, ${alpha(theme.palette.secondary.dark, 0.2)})`
                : `linear-gradient(135deg, ${alpha(theme.palette.primary.light, 0.1)}, ${alpha(theme.palette.secondary.light, 0.1)})`,
              border: `1px solid ${alpha(theme.palette.divider, theme?.aghrba?.tokens?.alpha?.border || 0.08)}`,
              position: 'relative',
              overflow: 'hidden',
              transition: theme?.aghrba?.tokens?.transitions?.standard || 'all 0.3s ease',
            }}
          >
            {/* Decorative elements */}
            <Box 
              sx={{ 
                position: 'absolute', 
                bottom: -10, 
                right: -10, 
                width: 150, 
                height: 150, 
                borderRadius: '50%', 
                background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.08)}, transparent 70%)`,
                zIndex: 0,
              }} 
            />
            
            <Grid container spacing={3} sx={{ position: 'relative', zIndex: 1 }}>
              <Grid item xs={12} md={7}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  <Avatar 
                    sx={{ 
                      bgcolor: alpha(theme.palette.info.main, 0.2),
                      color: theme.palette.info.main,
                      width: 50,
                      height: 50
                    }}
                  >
                    <HelpIcon fontSize="medium" />
                  </Avatar>
                  
                  <Box>
                    <Typography variant="h5" fontWeight={600} gutterBottom color="textPrimary">
                      Need Help with Analytics?
                    </Typography>
                    
                    <Typography variant="body1" paragraph color="textSecondary">
                      Our analytics tools provide comprehensive insights into your marketing performance.
                      If you need assistance interpreting the data or creating custom reports, our team is here to help.
                    </Typography>
                    
                    <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                      <Button 
                        variant="contained" 
                        color="primary" 
                        startIcon={<HelpIcon />}
                        sx={{ 
                          borderRadius: '8px',
                          fontWeight: 600,
                          boxShadow: `0 4px 14px 0 ${alpha(theme.palette.primary.main, 0.4)}`,
                        }}
                      >
                        Get Support
                      </Button>
                      
                      <Button 
                        variant="outlined" 
                        color="primary"
                        sx={{ 
                          borderRadius: '8px',
                          fontWeight: 600,
                        }}
                      >
                        View Documentation
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={5}>
                <Box 
                  sx={{ 
                    height: '100%', 
                    display: 'flex', 
                    flexDirection: 'column', 
                    justifyContent: 'center',
                    borderLeft: { md: `1px solid ${alpha(theme.palette.divider, 0.1)}` },
                    pl: { md: 3 },
                    mt: { xs: 2, md: 0 }
                  }}
                >
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom color="textPrimary">
                    Contact support team
                  </Typography>
                  
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 2,
                      p: 2,
                      mb: 2,
                      borderRadius: theme.shape.borderRadius,
                      background: alpha(theme.palette.background.paper, 0.5),
                      border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                    }}
                  >
                    <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}>
                      <EmailIcon fontSize="small" />
                    </Avatar>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 500,
                        color: theme.palette.primary.main,
                      }}
                    >
                      support@aghrba.com
                    </Typography>
                  </Box>
                  
                  <Chip 
                    icon={<StarIcon fontSize="small" />}
                    label="Premium support available 24/7" 
                    color="secondary"
                    sx={{ 
                      alignSelf: 'flex-start',
                      fontWeight: 500,
                      borderRadius: '16px',
                      '& .MuiChip-icon': {
                        color: 'inherit'
                      },
                    }}
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Fade>
      </Box>
    </PageContainer>
  );
};

export default AnalyticsIndex;